import { useState } from "react";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { api, formatError } from "../lib/api";
import { logger } from "../lib/logger";
import { Microscope, Loader2, Sparkles, Target, Zap } from "lucide-react";
import { CopyBtn } from "../components/CopyBtn";
import { useAuth } from "../contexts/AuthContext";
import { ShareCardActions } from "../components/ShareCardActions";

export default function AutopsyPage() {
  const { user, refreshUser } = useAuth();
  const [src, setSrc] = useState("");
  const [applyClone, setApplyClone] = useState(true);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  const analyze = async () => {
    setErr("");
    if (src.trim().length < 10) {
      setErr("Paste the viral text you want to dissect (at least a sentence).");
      return;
    }
    setLoading(true);
    setRes(null);
    try {
      const { data } = await api.post("/autopsy", { source_text: src, apply_clone: applyClone });
      setRes(data.autopsy);
      toast.success("Template extracted.");
      await refreshUser();
    } catch (e) {
      logger.error("Autopsy error", e);
      setErr(formatError(e.response?.data?.detail) || "Autopsy failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="autopsy-page">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 text-purple-600 px-3 py-1 text-xs font-bold tracking-wider uppercase mb-3">
            <Microscope className="w-3.5 h-3.5" /> Reverse-Engineer
          </div>
          <h1 className="text-4xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">
            Viral Autopsy
          </h1>
          <p className="text-zinc-500 mt-2 max-w-2xl">
            Paste a viral tweet, caption, hook, or script. CopyForge tears it apart into the psychological
            template behind it, then writes your own version — in your voice.
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-2 space-y-4">
            <textarea
              data-testid="autopsy-source"
              value={src}
              onChange={(e) => setSrc(e.target.value)}
              rows={10}
              placeholder="Paste the viral text you want to dissect (NOT a link — copy the words from the post)."
              className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-4 py-3 text-sm text-zinc-900 dark:text-white outline-none focus:border-brand resize-none"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="autopsy-clone-toggle">
              <input type="checkbox" checked={applyClone} onChange={(e) => setApplyClone(e.target.checked)} className="accent-brand" />
              <span className="text-zinc-700 dark:text-zinc-300">Write my version in my Clone's voice</span>
            </label>
            {err && (
              <p className="text-sm text-red-500" data-testid="autopsy-error">{err}</p>
            )}
            <button
              onClick={analyze}
              disabled={loading}
              data-testid="autopsy-run-btn"
              className="w-full bg-brand text-white font-semibold py-3.5 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Dissecting…</>) : (<><Microscope className="w-5 h-5" /> Dissect (1 cr)</>)}
            </button>
          </div>

          <div className="md:col-span-3">
            {!res && !loading && (
              <div className="h-full min-h-[320px] border-2 border-dashed border-zinc-200 dark:border-zinc-800 flex items-center justify-center p-8 text-center" data-testid="autopsy-empty">
                <div>
                  <Microscope className="w-8 h-8 text-zinc-300 dark:text-zinc-700 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">The forensic report appears here.</p>
                </div>
              </div>
            )}
            {loading && (
              <div className="h-full min-h-[320px] flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-brand mx-auto mb-3" />
                  <p className="text-sm text-zinc-500">Running viral forensics…</p>
                </div>
              </div>
            )}
            {res && (
              <div className="space-y-4" data-testid="autopsy-result">
                <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {res.hook_type && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-2 py-1">
                        {res.hook_type}
                      </span>
                    )}
                    {res.psychological_trigger && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-500 text-white px-2 py-1 flex items-center gap-1">
                        <Target className="w-3 h-3" /> {res.psychological_trigger}
                      </span>
                    )}
                  </div>
                  {res.why_it_worked && (
                    <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
                      {res.why_it_worked}
                    </p>
                  )}
                </div>

                {res.structural_template && (
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold tracking-wider uppercase text-zinc-400">Reusable Template</p>
                      <CopyBtn text={res.structural_template} label="Copy template" />
                    </div>
                    <pre className="whitespace-pre-wrap font-mono text-sm text-zinc-900 dark:text-zinc-100">{res.structural_template}</pre>
                  </div>
                )}

                {res.your_version && (
                  <div className="bg-gradient-to-br from-brand/5 to-purple-500/5 border border-brand/20 p-4" data-testid="autopsy-your-version">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold tracking-wider uppercase text-brand flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Your Version
                      </p>
                      <CopyBtn text={res.your_version} label="Copy" />
                    </div>
                    <p className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap leading-relaxed">
                      {res.your_version}
                    </p>
                  </div>
                )}

                {res.replication_prompt && (
                  <details className="bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-3">
                    <summary className="text-xs font-bold text-zinc-500 cursor-pointer">Advanced: replication prompt</summary>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-2 font-mono whitespace-pre-wrap">{res.replication_prompt}</p>
                  </details>
                )}

                <div className="pt-2">
                  <p className="text-xs font-bold tracking-wider uppercase text-zinc-400 mb-2">Share this dissection</p>
                  <ShareCardActions
                    kind="autopsy"
                    data={{
                      hook_type: res.hook_type,
                      psychological_trigger: res.psychological_trigger,
                      structural_template: res.structural_template,
                      your_version: res.your_version,
                    }}
                    refCode={user?.referral_code || ""}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Keep Zap import referenced to satisfy lint even if we stop using it below */}
      {false && <Zap />}
    </AppShell>
  );
}
